import { Assets, Graphics, Text, Application, Container, Texture, Sprite, TextStyle, ColorMatrixFilter } from 'pixi.js';
import { GifSprite, GifSource } from 'pixi.js/gif';
import { sound } from '@pixi/sound';
import { Point } from '../models/point';
import { PlayerCardsController } from './player-cards-controller';
import { CardData } from '../models/card-data';
import { VariantType2 } from '@app/enums/variant-type-2';
import { PlayerTimebankProgressBar } from './player-timebank-progress-bar';
import { PlayerStatus } from '../enums/player-status.enum';
import { CurrencyInfo } from '@app/models/currency-info';
import { GameCurrencyPipe } from '@app/pipes/game-currency.pipe';
import { GameCardSorting, GameStatus } from '@app/store/features/games/games.reducer';
import { ChatBubble } from './chat-bubble';
import * as GamesActions from '@app/store/features/games/games.actions';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { NoteColors } from '../enums/note-colors';
import { MemberPreferences } from '@app/models/member-profile';
import { CallTime } from './call-time';
import { PlayerCallTime } from '@app/models/player-call-time';
import { Card } from './card';
import { DragAndDrop } from '@app/helpers/drag-drop';
import { environment } from '@environments/environment';
import { CardReplaceInfo } from './card-replace-info';
import moment from 'moment';
import { CardSize } from '@app/store/features/config/config.reducer';
import { Subject, takeUntil } from 'rxjs';
import { EmoticonInfo } from '@app/models/emoticon-info';

export enum PlayerGameStatus {
    default = 'default',
    active = 'active',
    fold = 'fold',
    sitout = 'sitout',
    disconnected = 'disconnected',
    seating = 'seating'
}



export class Player {
    private destroy$ = new Subject<void>();

    private _store: Store;
    private chatBubbles: ChatBubble[] = [];
    private emoticonSprite: GifSprite | null = null;

    container = new Container();

    playerZoneWidth = 355
    playerZoneHeight = 295
    playerAvatarCircleSize = 150

    avatarOverlaySprite: Sprite;
    avatarOverlayBlinkSprite: Sprite;
    avatarPictureSprite: Sprite;

    infoBoxSprite: Sprite;
    infoBoxBlinkSprite: Sprite;

    playerAvatarText: Text;
    tableId: number;
    playerId: number;
    playerName: Text;
    playerMoney: {
        amount: Text,
        currency: Text,
        decimal: Text,
        value: number
    }
    playerNote: Sprite | undefined;

    defaultFontStyle: Partial<TextStyle> = {
        fontFamily: 'Saira Semi Condensed',
        fontSize: 40,
        fontWeight: '500',
        fill: '#ffffff',
    }

    straddleSprite: Sprite;
    dealerSprite: Sprite;
    proPlayerSprite: Sprite;

    timebank: {
        status: Text, // Timebank: 10
        timer: Text, // 6,5,4... countdown
        bar: PlayerTimebankProgressBar; // progress bar
        data: {
            timer: number,
            bankTime: number,
            normalTime: number,
            timeToPlay: number, // new implementation DRAW GAMES
            normalTimeExpired: boolean;
        }
    }

    disconnectionProtection: {
        overlay: Sprite;
        icon: Sprite;
        caption: Text;
        timer: Text;
        dateTimeExpiresAt: number; // timestamp
    }





    colorMatrix: ColorMatrixFilter;
    playerCardsController: PlayerCardsController;
    cardReplaceInfo: CardReplaceInfo;

    variant: VariantType2;
    alreadyBetInThisRound: number = 0;
    isMyTurn: boolean = false;
    status: PlayerStatus = PlayerStatus.Ready;
    hasFold: boolean = false;
    numberOfCardsHolding: number = 0;
    memberPreferences: MemberPreferences

    playerZone: Graphics;
    /**
     * Player Class Layers Indexes:
     * ===============================
     * Player Zone - 0
     * Avatar - 1
     * Info Box - 2
     * Dealer - 3
     * Straddle - 4
     * Timebank - 5
     *   
    */

    textures: {
        infoBox: {
            default: Texture,
            active: Texture,
            disconnected: Texture,
            fold: Texture,
            sitout: Texture
        },
        avatar: Texture,
        avatarOverlay: {
            default: Texture,
            active: Texture,
            disconnected: Texture,
            fold: Texture,
            sitout: Texture
        },
        playerStraddle: Texture,
        playerDealer: Texture,
        playerProMark: Texture,

        disconnectionProtection: {
            overlay: Texture,
            icon: Texture
        }

        // playerTimebank: { // not used in v4
        //     barBackground: Texture,
        //     barCap: Texture,
        //     barMiddle: Texture,
        //     barGlow: Texture
        // },
        playerNote: {
            [key: string]: Texture
        }
        chatBubble: Texture,
    }

    isPlayerTurn: boolean = false;
    isMyPlayer: boolean = false;
    showAmountInBigBlinds = false;
    currency: CurrencyInfo;


    name: string;
    playerNameMaxLength: number;


    seatNumber: number;
    fakeSeatNumber?: number;

    private balance = 0;

    private blinkAlpha = 0;
    private blinkSpeed = 0.8; // lower is slower, higher is faster
    private blinkDirection = 1;
    private shouldRevertToDefaultStatus = false;
    private revertTimer = 0;

    private callTime: CallTime;
    private cardSorting: GameCardSorting;

    private capMoney = 0;
    private capMoneyText = new Text('', { ...this.defaultFontStyle, fontSize: 34 });
    private isCapGame = false;
    private isHandReplay = false;

    private cards: CardData[] = [];
    constructor(
        store: Store,
        textures: {
            infoBox: {
                default: Texture,
                active: Texture,
                disconnected: Texture,
                fold: Texture,
                sitout: Texture
            },
            avatar: Texture,
            avatarOverlay: {
                default: Texture,
                active: Texture,
                disconnected: Texture,
                fold: Texture,
                sitout: Texture
            },
            playerStraddle: Texture,
            playerDealer: Texture,
            playerProMark: Texture,
            // playerTimebank: { // not used in v4
            //     barBackground: Texture,
            //     barCap: Texture,
            //     barMiddle: Texture,
            //     barGlow: Texture
            // },
            disconnectionProtection: {
                overlay: Texture,
                icon: Texture
            }
            playerNote: {
                [key: string]: Texture
            }
            chatBubble: Texture,
            callTime: {
                callTimeAttention: Texture;
                callTimeCheck: Texture;
                callTimeBackground: Texture;
            }
        },
        position: Point,
        data: {
            tableId: number,
            playerId: number,
            playerName: string,
            avatarUrl?: string
            currency: CurrencyInfo,
            balance: number, // Player Current Balance
            status: PlayerStatus,
            isProPlayer: boolean,
            timebank: {
                normalTime: number
            }
            gamePositions: {
                playerNameMaxLength: number,
            },
            isMyPlayer: boolean,
            seatNumber: number,
            cards: CardData[],
            memberPreferences: MemberPreferences,
            playerCallTime?: PlayerCallTime,
            variant: VariantType2,
            postedStraddle: boolean,
            cardSorting: GameCardSorting
            capMoney: number,
            isCapGame: boolean,
            cardSize: CardSize,
            isHandReplay: boolean,
        }

    ) {

        this._store = store;
        this.currency = { ...data.currency, withoutDecimal: false }
        this.textures = textures;
        this.tableId = data.tableId;
        this.playerId = data.playerId
        this.name = data.playerName
        this.playerNameMaxLength = data.gamePositions.playerNameMaxLength;
        this.isMyPlayer = data.isMyPlayer;
        this.seatNumber = data.seatNumber;
        this.memberPreferences = data.memberPreferences;
        this.variant = data.variant;
        this.cardSorting = data.cardSorting;
        this.capMoney = data.capMoney;
        this.isCapGame = data.isCapGame;
        this.isHandReplay = data.isHandReplay;
        this.createPlayer(textures, data.isProPlayer, data.playerName, data.gamePositions.playerNameMaxLength, data.timebank, data.cardSize, data.avatarUrl) // extract to method createPlayer mainly because of async

        if (this.memberPreferences.playerColor[this.playerId]) {
            this._setPlayerNote(this.memberPreferences.playerColor[this.playerId])
        }
        if (data.postedStraddle) {
            this.straddleSprite.visible = true;
        }

        this.callTime = new CallTime(textures.callTime, { playerCallTime: data.playerCallTime })

        this.callTime.container.position.set(this.playerZoneWidth / 2 - this.callTime.width / 2, 0);

        this.container.addChild(this.callTime.container);

        this.updateBalance(data.balance)
        this.updateStatus(data.status)



        if (this.variant)

            if (data.cards.length > 0) {
                this.receiveCards(data.cards);
            }
        this.container.position.set(position.x, position.y);

        this.container.name = `Player #${this.playerId} ${this.name}`;


    }



    updateCardSorting(cardSorting: GameCardSorting) {
        this.cardSorting = cardSorting;
        /**
         * @description We cannot sort hidden cards
         */
        if (this.isMyPlayer) {
            this.playerCardsController.updateCardSorting(cardSorting)
        }
    }

    updateVariant(variant: VariantType2) {
        const currIsRotated = !(this.variant === VariantType2.sevenStud || this.variant === VariantType2.sevenStudHiLo || this.variant === VariantType2.razz || this.variant === VariantType2.twoSevenRazz) && !this.isMyPlayer;
        const newIsRotated = !(variant === VariantType2.sevenStud || variant === VariantType2.sevenStudHiLo || variant === VariantType2.razz || variant === VariantType2.twoSevenRazz) && !this.isMyPlayer;

        // Update rotation
        if (currIsRotated !== newIsRotated) {
            this.receiveCards(this.cards);
        }

        this.variant = variant;
    }

    updateDataPlayerCallTime(playerCallTime?: PlayerCallTime) {
        this.callTime.updateDataPlayerCallTime(playerCallTime)
    }
    // 6'Clock
    changeSeatPosition(position: Point, seatNumber: number) {
        if (this.seatNumber !== seatNumber) {
            this.fakeSeatNumber = seatNumber
        } else {
            this.fakeSeatNumber = undefined;
        }
        this.container.position.set(position.x, position.y);
    }

    updateStatus(status: PlayerStatus) {
        if (status === PlayerStatus.Sitout) {
            this.setStatus(PlayerGameStatus.sitout);
        } else if (status === PlayerStatus.LeaveSeat) {
            this.setStatus(PlayerGameStatus.sitout);
        } else if (status === PlayerStatus.LeftNextHand) {
            this.setStatus(PlayerGameStatus.sitout);
        } else if (status === PlayerStatus.Ready) {
            this.setStatus(PlayerGameStatus.default);
        } else if (status === PlayerStatus.Disconnected) {
            this.setStatus(PlayerGameStatus.disconnected);
        }
    }

    update(dt: number) {

        // ⏺ CALL TIME 
        // this.timebank.data.timer += dt;
        // this.timebank.bar.update(1 - (this.timebank.data.timer / this.timebank.data.bankTime));
        // this.timebank.status.text = Math.round((this.timebank.data.bankTime - this.timebank.data.timer) / 1000).toString();


        // ------

        if (this.disconnectionProtection?.dateTimeExpiresAt > 0) {
            const diff = moment(this.disconnectionProtection.dateTimeExpiresAt).diff(moment())
            if (diff < 0) {
                this.unsetDisconnectionProtection();
            } else {
                const formatTime = moment.utc(diff).format("mm:ss");
                this.disconnectionProtection.timer.text = formatTime;
            }
        }

        // bar.container.visible is false once stopTimer() ran - without this guard
        // the countdown would restart from full normalTime after a stop (e.g. fold
        // on timebank expiry) while waiting for the next playerTurnChange/endTurn.
        if (this.isMyTurn && !this.isHandReplay ) {

            this.timebank.data.timer += dt;
            if (this.timebank.data.timeToPlay > 0) {

                if (this.timebank.data.timer <= this.timebank.data.timeToPlay) {

                    this.timebank.bar.update(1 - (this.timebank.data.timer / this.timebank.data.timeToPlay));
                    this.playerName.text = `${Math.max(0, Math.ceil((this.timebank.data.timeToPlay - this.timebank.data.timer) / 1000))}s`
                } else {
                    // Keep timeToPlay set and stay pinned at 0s here. Zeroing it
                    // would fall through to the normalTime branch next frame and
                    // restart the countdown from full normalTime (the "35s" flash)
                    // until the server event (fold/turn change) calls endTurn().
                    // Timebank countdown only starts via TimeBankUsing -> startUsingTimeBank.
                    this.timebank.bar.update(0);
                    this.playerName.text = '0s';
                }
            }
            else if (!this.timebank.data.normalTimeExpired) {
                if (this.timebank.data.timer <= this.timebank.data.normalTime) {
                    this.timebank.bar.update(1 - (this.timebank.data.timer / this.timebank.data.normalTime));
                    this.playerName.text = `${Math.max(0, Math.ceil((this.timebank.data.normalTime - this.timebank.data.timer) / 1000))}s`
                } else {
                    this.timebank.bar.update(0);
                }
            } else {
                if (this.timebank.data.timer <= this.timebank.data.bankTime) {
                    this.timebank.bar.update(1 - (this.timebank.data.timer / this.timebank.data.bankTime));
                    this.playerName.text = `${Math.round((this.timebank.data.bankTime - this.timebank.data.timer) / 1000)}s`
                } else {
                    this.timebank.bar.update(0);
                
                }
            }
            if (this.blinkAlpha <= 0) {
                this.blinkDirection = 1
                this.blinkAlpha = 0
            } else if (this.blinkAlpha >= 1) {
                this.blinkDirection = -1
                this.blinkAlpha = 1
            }

            if (this.blinkDirection === 1) {
                this.blinkAlpha += (dt / 1000) * this.blinkSpeed;
            } else {
                this.blinkAlpha -= (dt / 1000) * this.blinkSpeed;
            }

            this.infoBoxBlinkSprite.alpha = this.blinkAlpha;
            this.avatarOverlayBlinkSprite.alpha = this.blinkAlpha;
        }

        if (this.shouldRevertToDefaultStatus) {
            this.revertTimer += dt;
            if (this.revertTimer >= 2000) {
                this.setStatusText(this.name);
                this.shouldRevertToDefaultStatus = false;
                this.revertTimer = 0;
            }
        }

        // update all cards
        this.playerCardsController.update(dt);


        // ⏺
        // if (this.cardsControllerAdded && !this.showBigCardsActive) {
        //     this.updateCardControllIndex();
        // }

        this.callTime.update(dt);
    }

    async createPlayer(
        textures: {
            infoBox: { default: Texture, active: Texture },
            avatar: Texture,
            avatarOverlay: { default: Texture, active: Texture }
            playerStraddle: Texture,
            playerDealer: Texture,
            playerProMark: Texture,
        },
        isProPlayer: boolean,
        playerName: string,
        playerNameMaxLength: number,
        timebank: {
            normalTime: number
        },
        cardSize: CardSize,
        avatarUrl?: string,
    ) {

        this.colorMatrix = new ColorMatrixFilter();
        this.colorMatrix.greyscale(0.4, false);

        this._createPlayerZone();
        this._createAvatar({ avatar: textures.avatar, avatarOverlay: textures.avatarOverlay }, { avatarUrl });


        this.playerCardsController = new PlayerCardsController(cardSize)
        this.container.addChild(this.playerCardsController.container);

        this._createInfoBox(textures.infoBox)

        this._createInfoBoxText(playerName, playerNameMaxLength);

        this._createDealer(textures.playerDealer);
        this._createStraddle(textures.playerStraddle);

        this._createProPlayer(textures.playerProMark, isProPlayer);


        this._createTimebank(timebank)


        // this.avatarOverlaySprite.cursor = 'pointer';
        // this.avatarOverlaySprite.eventMode = 'static';
        // this.avatarOverlaySprite.on('pointerdown', (event) => {

        // })

        if (!this.isMyPlayer) {
            this.infoBoxSprite.cursor = 'pointer';
            this.infoBoxSprite.eventMode = 'static' // ℹ️ Emit events and is hit tested. Same as interaction = true in v7, useful for objects like buttons that do not move. https://pixijs.com/8.x/guides/components/interaction
        }


        this.infoBoxSprite.on('pointerdown', (event) => { // ℹ️ pointerdown Fired when a pointer device button is pressed on the display object.
            if (this.isMyPlayer) {
                this.playerCardsController.expandCollapseCards()
            } else {
                this._onOpenUserInfo()
            }

        })


        this.cardReplaceInfo = new CardReplaceInfo();
        this.container.addChild(this.cardReplaceInfo.container);


        this.updateCAPMoney(this.capMoney)
        this.container.addChild(this.capMoneyText);

        if (this.isMyPlayer) {
            this.playerCardsController.cardsSelected$
                .pipe(takeUntil(this.destroy$))
                .subscribe((data) => {
                    this._store.dispatch(GamesActions.onSelectedCards({ idTable: this.tableId, cards: data.cards, indexes: data.indexes }))
                })

        }


        // TODO: 📖 add player Cards Controller

        // TODO: 📖 add TimeBank Progress Bar class  


        // test:
        // this.infoBoxSprite.filters = [this.colorMatrix];
        // this.avatarPictureSprite.filters = [this.colorMatrix];

        // 🛠️ DEV TOOL
        if (environment.devMode) {
            new DragAndDrop(this.container);
        }
    }

    /**
   * @description
   * Draw games on Restore cards selection
   */
    markCardsAsSelected(cards: CardData[]) {
        this.playerCardsController.markCardsAsSelected(cards)
    }

    setPlayerCardsSelectionStatus(isSelectable: boolean, portraitMode: boolean) {
        if (portraitMode) {
            if (isSelectable) {
                this.infoBoxSprite.cursor = 'pointer';
                this.infoBoxSprite.eventMode = 'static'
                this.playerCardsController.setCardsSelectionStatus(true);
            } else {
                this.infoBoxSprite.cursor = 'default';
                this.infoBoxSprite.eventMode = 'none';
                this.playerCardsController.collapseCards()
                this.playerCardsController.setCardsSelectionStatus(false);
            }
        } else {
            this.playerCardsController.collapseCards()
            this.playerCardsController.setCardsSelectionStatus(isSelectable);
        }
    }

    setPlayerReplacedCardsNumber(numOfReplacedCards: number) {
        this.cardReplaceInfo.setReplacedCardsNumber(numOfReplacedCards)
        this.cardReplaceInfo.container.position.set(this.playerZoneWidth / 2 - this.cardReplaceInfo.container.width / 2, this.playerZoneHeight - this.cardReplaceInfo.container.height - 2);
    }

    setPlayerAllReplacedCardsNumbersInactive() {
        this.cardReplaceInfo.setAllNumbersInactive()
    }

    removePlayerReplacedCardsNumber() {
        this.cardReplaceInfo.removeReplacedCardsNumber()
    }

    _createPlayerZone() {
        // ## Player zone
        this.playerZone = new Graphics();
        this.playerZone.roundRect(
            0, // x
            0, // y
            this.playerZoneWidth, // Width
            this.playerZoneHeight, // Height
            0 // Radius
        ).fill({ color: 0x0025FF, alpha: 0.5 });
        this.playerZone.alpha = 0 // in debug mode it can be 1, move to enviroment variable
        this.container.addChild(this.playerZone)
    }

    _createAvatar(textures: {
        avatar: Texture,
        avatarOverlay: { default: Texture, active: Texture }

    }, data: { avatarUrl?: string }) {



        const offsetTop = this.playerZoneHeight * 0.1
        // ## Avatar Circle Mask
        const circleWidth = this.playerAvatarCircleSize
        const avatarMask = new Graphics();
        avatarMask.circle(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop, circleWidth / 2).fill({ color: 0x00FF00, alpha: 1 }); // x and y center of the circle
        this.container.addChild(avatarMask);
        // avatarMask.isMask = true; // Removed: not needed in PixiJS 8, mask is set via sprite.mask

        // ## Avatar Circle Background - Default theme Black Color behind avatar if not loaded
        const avatarBorder = new Graphics();
        avatarBorder.circle(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop, circleWidth / 2).fill({ color: 0x000000, alpha: 1 }); // x and y center of the circle
        this.container.addChild(avatarBorder);

        // ## Avatar Picture Sprite Image
        this.avatarPictureSprite = new Sprite(textures.avatar);
        this.avatarPictureSprite.anchor.set(0.5);
        this.avatarPictureSprite.position.set(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop)
        this.avatarPictureSprite.mask = avatarMask;
        this.avatarPictureSprite.scale.set(circleWidth / this.avatarPictureSprite.texture.width)
        this.container.addChild(this.avatarPictureSprite);



        if (data.avatarUrl) {
            Assets.load(data.avatarUrl)
                .then(() => this.avatarPictureSprite.texture = Assets.get(data.avatarUrl!))
                .catch((error) => console.error('Greska kod load', error))
        }

        // ## Avatar Default Overlay Background
        this.avatarOverlaySprite = new Sprite(textures.avatarOverlay.default);
        this.avatarOverlaySprite.anchor.set(0.5);
        this.avatarOverlaySprite.position.set(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop)
        this.avatarOverlaySprite.mask = avatarMask;
        this.avatarOverlaySprite.scale.set(circleWidth / this.avatarOverlaySprite.texture.width)
        this.container.addChild(this.avatarOverlaySprite);

        // ## Avatar Active Blink Overlay Background
        this.avatarOverlayBlinkSprite = new Sprite(textures.avatarOverlay.active);
        this.avatarOverlayBlinkSprite.anchor.set(0.5);
        this.avatarOverlayBlinkSprite.position.set(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop)
        this.avatarOverlayBlinkSprite.mask = avatarMask;
        this.avatarOverlayBlinkSprite.scale.set(circleWidth / this.avatarOverlayBlinkSprite.texture.width)
        this.avatarOverlayBlinkSprite.visible = false;
        this.container.addChild(this.avatarOverlayBlinkSprite);



        // ## Disconnect Protection 
        this.disconnectionProtection = {
            overlay: new Sprite(this.textures.disconnectionProtection.overlay),
            icon: new Sprite(this.textures.disconnectionProtection.icon),
            caption: new Text('DISCONNECTION \n PROTECTION', {
                fontFamily: 'Saira Semi Condensed',
                fontSize: 14,
                fontWeight: '500',
                fill: '#ffffff',
                align: 'center'
            }),
            timer: new Text('00:20', {
                fontFamily: 'Saira Semi Condensed',
                fontSize: 36,
                fontWeight: '500',
                fill: '#ffffff',
                align: 'center'
            }),
            dateTimeExpiresAt: 0
        }

        this.disconnectionProtection.overlay.anchor.set(0.5);
        this.disconnectionProtection.overlay.position.set(this.playerZoneWidth / 2, circleWidth / 2 + offsetTop)
        this.disconnectionProtection.overlay.scale.set(circleWidth / this.disconnectionProtection.overlay.texture.width)
        this.disconnectionProtection.overlay.alpha = 0.9
        this.disconnectionProtection.overlay.mask = avatarMask;

        this.disconnectionProtection.icon.anchor.set(0.5);
        this.disconnectionProtection.icon.scale.set(0.6)
        this.disconnectionProtection.icon.position.set(this.playerZoneWidth / 2, 40)


        this.disconnectionProtection.caption.anchor.set(0.5, 0.5);
        this.disconnectionProtection.caption.position.set(this.playerZoneWidth / 2, circleWidth / 2 + 48);

        this.disconnectionProtection.timer.anchor.set(0.5, 0.5);
        this.disconnectionProtection.timer.position.set(this.playerZoneWidth / 2, 84);

        this.playerAvatarText = new Text('Sitout', { ...this.defaultFontStyle, fontSize: 32 });
        this.playerAvatarText.anchor.set(0.5, 0.5);
        this.playerAvatarText.position.set(this.playerZoneWidth / 2, circleWidth / 2 + 20);
        this.playerAvatarText.alpha = 0;
        this.container.addChild(this.playerAvatarText);


        this.unsetDisconnectionProtection()
        this.container.addChild(this.disconnectionProtection.overlay);
        this.container.addChild(this.disconnectionProtection.icon);
        this.container.addChild(this.disconnectionProtection.caption);
        this.container.addChild(this.disconnectionProtection.timer);

    }

    setDisconnectionProtection(dateTimeExpiresAt: number) {
        this.disconnectionProtection.dateTimeExpiresAt = dateTimeExpiresAt;
        this.disconnectionProtection.icon.visible = true
        this.disconnectionProtection.overlay.visible = true
        this.disconnectionProtection.caption.visible = true;
        this.disconnectionProtection.timer.visible = true;

    }

    unsetDisconnectionProtection() {
        this.disconnectionProtection.dateTimeExpiresAt = 0
        this.disconnectionProtection.icon.visible = false
        this.disconnectionProtection.overlay.visible = false
        this.disconnectionProtection.caption.visible = false;
        this.disconnectionProtection.timer.visible = false;

    }

    private _createInfoBox(textures: { default: Texture, active: Texture }) {
        const offsetBottom = this.playerZoneHeight * 0.1; // 10% from bottom and top

        const infoBoxWidth = 260

        this.infoBoxSprite = new Sprite(textures.default);
        this.infoBoxSprite.scale.set(infoBoxWidth / this.infoBoxSprite.texture.width)
        this.infoBoxSprite.position.set((this.playerZoneWidth - this.infoBoxSprite.width) / 2, this.playerZoneHeight - this.infoBoxSprite.height - offsetBottom);
        this.container.addChild(this.infoBoxSprite);


        this.infoBoxBlinkSprite = new Sprite(textures.active);
        this.infoBoxBlinkSprite.scale.set(infoBoxWidth / this.infoBoxSprite.texture.width)
        this.infoBoxBlinkSprite.position.set((this.playerZoneWidth - this.infoBoxSprite.width) / 2, this.playerZoneHeight - this.infoBoxSprite.height - offsetBottom);
        this.container.addChild(this.infoBoxBlinkSprite);
        this.infoBoxBlinkSprite.visible = false;

        // TO DO: 📖 Add events if its needed
        // if (this.gameComponent.getMemberProfile().Id !== this.id) {
        //     this.infoBoxSprite.interactive = true;
        //     this.infoBoxSprite.buttonMode = true;
        //     this.infoBoxSprite.cursor = 'pointer';
        //     this.infoBoxSprite.on('mousedown', (event) => {
        //         event.stopPropagation();
        //         this.gameComponent.clickPlayer(this.id, this.noteText, this.noteColor, this.realNameString);
        //     });
        //     this.infoBoxSprite.on('touchstart', (event) => {
        //         event.stopPropagation();
        //         this.gameComponent.clickPlayer(this.id, this.noteText, this.noteColor, this.realNameString);
        //     });
        // }
    }



    private _createInfoBoxText(playerName: string, playerNameMaxLength: number) {
        const offsetBottom = this.playerZoneHeight * 0.1; // 10% from bottom and top

        // # Player Name
        this.playerName = new Text(this.formatString(playerName, playerNameMaxLength), { ...this.defaultFontStyle, fontSize: 28 });
        this.playerName.name = 'playerName';
        this.playerName.anchor.set(0.5, 0.5);
        this.playerName.position.set(this.playerZoneWidth / 2, this.playerZoneHeight - this.infoBoxSprite.height + this.infoBoxSprite.height / 4 + this.infoBoxSprite.height * 0.05 - offsetBottom);

        this.container.addChild(this.playerName);
        // ----------------------------

        // # Player Money - Amount, Currency, Decimal
        this.playerMoney = {
            amount: new Text('', { ...this.defaultFontStyle, fontSize: 28 }),
            currency: new Text('', { ...this.defaultFontStyle, fontSize: 28 }),
            decimal: new Text('', { ...this.defaultFontStyle, fontSize: 22 }),
            value: 0
        }
        this.playerMoney.amount.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - offsetBottom
        this.playerMoney.amount.anchor.set(0.5, 1);

        this.playerMoney.currency.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - offsetBottom
        this.playerMoney.currency.anchor.set(1, 1);
        this.playerMoney.currency.alpha = 0.5;


        this.playerMoney.decimal.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - 1 - offsetBottom;// 1px small offset up because of the smaller font size
        this.playerMoney.decimal.anchor.set(0, 1);

        this.container.addChild(this.playerMoney.amount);
        this.container.addChild(this.playerMoney.decimal);
        this.container.addChild(this.playerMoney.currency);
        // ----------------------------
        // test: this.updatePlayerMoney('1', '40','$')

    }

    private _createDealer(texture: Texture) {
        this.dealerSprite = new Sprite(texture);
        this.dealerSprite.name = 'dealer';
        this.dealerSprite.visible = false;
        this.dealerSprite.anchor.set(0.5, 0.5);
        this.dealerSprite.scale.set(0.6)
        this.dealerSprite.position.set(this.playerZoneWidth / 2 - this.infoBoxSprite.width / 2, this.playerZoneHeight - this.infoBoxSprite.height);

        this.container.addChild(this.dealerSprite);

    }

    private _createStraddle(texture: Texture) {
        this.straddleSprite = new Sprite(texture);
        this.straddleSprite.name = 'straddle';
        this.straddleSprite.visible = false;
        this.straddleSprite.anchor.set(0.5, 0.5);
        this.straddleSprite.scale.set(0.6)
        this.straddleSprite.position.set(this.playerZoneWidth / 2 + this.infoBoxSprite.width / 2, this.playerZoneHeight - this.infoBoxSprite.height);

        this.container.addChild(this.straddleSprite);

    }

    private _createProPlayer(texture: Texture, isProPlayer: boolean) {
        this.proPlayerSprite = new Sprite(texture);
        this.proPlayerSprite.name = 'proPlayer';
        this.proPlayerSprite.visible = isProPlayer;
        this.proPlayerSprite.anchor.set(0.5, 0.5);
        this.proPlayerSprite.scale.set(0.6)
        this.proPlayerSprite.position.set(this.playerZoneWidth / 2 + this.infoBoxSprite.width / 2, this.playerZoneHeight - this.playerZoneHeight * 0.12);

        this.container.addChild(this.proPlayerSprite);

    }




    private _createTimebank(timebank: {
        normalTime: number
    },) {

        this.timebank = {
            status: new Text('Timebank: 0', { ...this.defaultFontStyle, fill: '#000000', fontSize: 22 }),
            timer: new Text('10', { ...this.defaultFontStyle, fontSize: 72, dropShadow: { color: '#000000', blur: 2, distance: 5, alpha: 1, angle: 90 } }),
            bar: new PlayerTimebankProgressBar({ x: 52, y: 210 }),
            data: {
                timeToPlay: 0,
                timer: 10000,
                bankTime: 50000,
                normalTime: timebank.normalTime, // TO DO its hardcorded for now
                normalTimeExpired: false // TO DO its hardcorded for now
            }
        }

        this.timebank.status.anchor.set(0.5, 1);
        this.timebank.status.position.set(this.playerZoneWidth / 2, this.playerZoneHeight);


        this.timebank.status.visible = false;
        this.timebank.bar.container.visible = false;

        if (!this.isHandReplay) {
            this.container.addChild(this.timebank.status);
            this.container.addChild(this.timebank.bar.container);
        }
        this.timebank.bar.reset();
    }



    updatePlayerMoney(currencySymbol: string, amount: string, decimal = '') {
        this.playerMoney.amount.text = amount;
        this.playerMoney.currency.text = currencySymbol;
        this.playerMoney.decimal.text = decimal ? `.${decimal}` : '';

        let offsetBottom = this.playerZoneHeight * 0.1; // 10% from bottom and top


        if (this.isCapGame) {
            this.playerName.style.fontSize = 24
            this.playerName.position.set(this.playerZoneWidth / 2, this.playerZoneHeight - this.infoBoxSprite.height + this.infoBoxSprite.height / 4 + this.infoBoxSprite.height * 0.05 - offsetBottom - 20);

            this.playerMoney.amount.style.fontSize = 34
            this.playerMoney.currency.style.fontSize = 34
            this.playerMoney.currency.alpha = 1;
            this.playerMoney.decimal.style.fontSize = 28;

            this.playerMoney.currency.text = `( ${this.playerMoney.currency.text}`;
            this.playerMoney.decimal.text = `${this.playerMoney.decimal.text} )`;

            offsetBottom -= 4;
        } else {
            this.playerName.style.fontSize = 28
            this.playerName.position.set(this.playerZoneWidth / 2, this.playerZoneHeight - this.infoBoxSprite.height + this.infoBoxSprite.height / 4 + this.infoBoxSprite.height * 0.05 - offsetBottom);

            this.playerMoney.amount.style.fontSize = 34
            this.playerMoney.currency.style.fontSize = 34
            this.playerMoney.currency.alpha = 0.5;
            this.playerMoney.decimal.style.fontSize = 28

            offsetBottom -= 4;
        }

        this.playerMoney.amount.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - offsetBottom
        this.playerMoney.currency.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - offsetBottom
        this.playerMoney.decimal.position.y = this.playerZoneHeight - this.infoBoxSprite.height * 0.1 - 1 - offsetBottom;// 1px small offset up because of the smaller font size


        this.playerMoney.amount.position.x = this.playerZoneWidth / 2 + this.playerMoney.currency.width / 2 - this.playerMoney.decimal.width / 2
        this.playerMoney.currency.position.x = this.playerMoney.amount.position.x - this.playerMoney.amount.width / 2 - 5 // 5px is space
        this.playerMoney.decimal.position.x = this.playerMoney.amount.position.x + this.playerMoney.amount.width / 2

    }




    private formatString(str: string, maxLength: number): string {
        if (str.length > maxLength) {
            return str.substring(0, maxLength) + '..';
        } else {
            return str;
        }
    }



    // ++++++++++++++++++++++++++++++++++++++++++++++++

    removeCards() {
        this.hasFold = false;
        this.numberOfCardsHolding = 0;
        this.playerCardsController.removeAllCards();
    }

    foldCards() {
        this.hasFold = true;
        this.playerCardsController.removeAllCards();
        this.numberOfCardsHolding = 0;
        // endTurn (not just stopTimer) so isMyTurn is cleared; otherwise update()
        // restarts the normal-time countdown (e.g. flashes "35s") until the next
        // playerTurnChange arrives and calls endTurn.
        this.endTurn();
    }

    updateBalance(balance: number) {
        let money = 0;
        if (balance !== undefined) {
            money = balance;
        }
        this.balance = money;

        if (this.showAmountInBigBlinds) {
            // ⏺  to do
        } else {
            this.playerMoney.value = money;



            const { amount, symbol, decimal } = this.formatMoney(money, this.currency);
            this.updatePlayerMoney(symbol, amount, decimal)
        }

        this.setPlayerMoneyVisible(true);

        if (money > 0) {
            this.playerName.text = this.formatString(this.name, this.playerNameMaxLength);
        }
    }

    updateIsCapGame(isCapGame: boolean) {
        this.isCapGame = isCapGame;
        this.capMoneyText.visible = this.isCapGame;

        const currency = { ...this.currency, withoutDecimal: false }
        const { amount, symbol, decimal } = this.formatMoney(this.playerMoney.value, currency);
        this.updatePlayerMoney(symbol, amount, decimal)
    }

    private formatMoney(money: number, currency: CurrencyInfo) {
        const minimumFractionDigits = currency ? currency.nbDecimalVisible : 0;
        const maximumFractionDigits = currency ? currency.nbDecimalVisible : 0

        const total = (money / currency.multiplier).toLocaleString('en-US', { minimumFractionDigits, maximumFractionDigits });
        const amount = total.split('.')[0] ? `${total.split('.')[0]}` : '';
        const decimal = total.split('.')[1] ? `${total.split('.')[1]}` : '';
        const symbol = currency ? currency.symbol : ''

        return { total, amount, decimal, symbol }
    }

    updateCAPMoney(capMoney: number) {
        this.capMoney = capMoney;
        this.capMoneyText.visible = this.isCapGame;

        const currency = { ...this.currency, withoutDecimal: false }
        this.capMoneyText.text = GameCurrencyPipe.prototype.transform(this.capMoney, currency);
        this.capMoneyText.position.y = this.playerZoneHeight - this.infoBoxSprite.height + this.capMoneyText.height - 34;
        this.capMoneyText.position.x = this.playerZoneWidth / 2 - this.capMoneyText.width / 2;

    }

    setStatusText(statusText: string) {
        if (statusText) {
            this.playerName.text = statusText;
            this.shouldRevertToDefaultStatus = true;
            this.revertTimer = 0;
        }
    }

    receiveCards(cards: CardData[]) {
        this.cards = cards;
        this.playerCardsController.removeAllCards();
        const revealedCards = cards.filter(card => card.isHidden).length === 0;
        const isRotated = !revealedCards && !(this.variant === VariantType2.sevenStud || this.variant === VariantType2.sevenStudHiLo || this.variant === VariantType2.razz || this.variant === VariantType2.twoSevenRazz) && !this.isMyPlayer;

        const cardSorting = this.isMyPlayer ? this.cardSorting : GameCardSorting.Default; // 📚 We cannot sort hidden cards
        this.playerCardsController.addCards(cards, cardSorting, isRotated);
    }




    receiveR2TCards(cards: CardData[]) {
        this.playerCardsController.addR2TCards(cards);
    }



    setTimeToPlay(timeToPlaySeconds: number, timer?: number) {
        this.stopTimer('setTimeToPlay')
        this.timebank.bar.container.visible = true;
        this.timebank.bar.reset();
        this.timebank.data.timeToPlay = timeToPlaySeconds * 1000
        if (timer !== undefined) {
            this.timebank.data.timer = timer * 1000
        }

    }
    // Bank Time Actions
    setTimer(newTime: number) {
        this.timebank.bar.reset();
        this.timebank.data.normalTimeExpired = false;
        this.timebank.data.timeToPlay = Math.max(0, newTime) * 1000;
        this.timebank.data.timer = 0;
        this.timebank.bar.container.visible = true;
    }

    setBankTime(bankTime: number = 0) {
        this.timebank.data.bankTime = bankTime * 1000;
        this.timebank.status.text = `Timebank: ${bankTime}`;
    }

    startUsingTimeBank(bankTime: number) {
        // Ignore duplicate TimeBankUsing events for the same turn.
        // Re-initializing here causes visible jumps (e.g. back to full bank value).
        if (this.timebank.data.normalTimeExpired) {
            return;
        }

        this.timebank.data.bankTime = bankTime * 1000;
        this.timebank.data.timeToPlay = 0;
        this.timebank.data.normalTimeExpired = true;
        this.timebank.data.timer = 0;


        this.timebank.status.text = `Timebank: ${bankTime}`;


        this.playerName.text = `${bankTime}s`

        this.timebank.bar.changeToTimebankColor();
        this.timebank.bar.reset();
    }

    stopTimer(place: string) {

        this.timebank.data.timeToPlay = 0;
        this.timebank.data.normalTimeExpired = false;

        this.timebank.data.timer = 0;
        this.timebank.bar.changeToDefaultColor()
        this.timebank.bar.reset()
        this.timebank.bar.container.visible = false;

        this.timebank.status.visible = false;
        this.playerName.text = this.name

        this.infoBoxBlinkSprite.visible = false;
        this.avatarOverlayBlinkSprite.visible = false;

        this.blinkAlpha = 1;
    }

    //

    setPlayerMoneyVisible(visible: boolean) {
        this.playerMoney.amount.visible = visible;
        this.playerMoney.currency.visible = visible;
        this.playerMoney.decimal.visible = visible;
    }

    setStatus(playerGameStatus: PlayerGameStatus) {
        this.playerAvatarText.alpha = 0;

        switch (playerGameStatus) {
            case PlayerGameStatus.seating:
                this.infoBoxSprite.texture = this.textures.infoBox.default;
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.default;

                this.infoBoxSprite.filters = [];
                this.avatarOverlaySprite.filters = [];
                this.avatarPictureSprite.filters = [];

                break;
            case PlayerGameStatus.active:
                this.infoBoxSprite.texture = this.textures.infoBox.active;
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.active;

                this.infoBoxSprite.filters = [];
                this.avatarOverlaySprite.filters = [];
                this.avatarPictureSprite.filters = [];

                this.setPlayerMoneyVisible(true);
                break;
            case PlayerGameStatus.fold:
                this.infoBoxSprite.texture = this.textures.infoBox.fold
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.fold
                break;
            case PlayerGameStatus.sitout:
                this.infoBoxSprite.texture = this.textures.infoBox.sitout
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.sitout

                this.infoBoxSprite.filters = [this.colorMatrix];
                this.avatarOverlaySprite.filters = [this.colorMatrix];
                this.avatarPictureSprite.filters = [this.colorMatrix];

                this.playerAvatarText.text = 'Sitout';
                this.playerAvatarText.alpha = 1;

                // this.setPlayerMoneyVisible(true);
                // this.setStatusText('Sitout');

                break;
            case PlayerGameStatus.disconnected:
                this.infoBoxSprite.texture = this.textures.infoBox.disconnected
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.disconnected

                this.infoBoxSprite.filters = [];
                this.avatarOverlaySprite.filters = [];
                this.avatarPictureSprite.filters = [];

                // this.setPlayerMoneyVisible(false);
                this.setStatusText('Disconnected');

                break;
            default:
                this.infoBoxSprite.texture = this.textures.infoBox.default
                this.avatarOverlaySprite.texture = this.textures.avatarOverlay.default

                this.infoBoxSprite.filters = [];
                this.avatarOverlaySprite.filters = [];
                this.avatarPictureSprite.filters = [];


                break;
        }
    }


    takeTurn(isReplay?: boolean) {
        if (isReplay) {
            return;
        }
        this.isMyTurn = true;
        this.timebank.bar.container.visible = true;

        // Blinking Sprites
        this.infoBoxBlinkSprite.visible = true;
        this.avatarOverlayBlinkSprite.visible = true;

        if (this.isMyPlayer) {

            this.timebank.status.visible = true;
        }
    }

    endTurn() {
        this.isMyTurn = false;
        this.isPlayerTurn = false;
        this.stopTimer('endTurn')
    }



    chatMessage(message: string, portraitMode = false) {
        let maxMessageLength = portraitMode ? 16 : 20;


        if (message.split(' ').length === 1) {
            maxMessageLength = portraitMode ? 8 : 10;
        }

        const truncatedMessage = message.length > maxMessageLength
            ? `${message.slice(0, maxMessageLength).trimEnd()}...`
            : message;

        const position: Point = { x: 180, y: 0 };
        const chatBubble = new ChatBubble(this.textures.chatBubble, position, {
            message: truncatedMessage,
            isVerticalLayout: portraitMode,
        });
        this.chatBubbles.push(chatBubble);
        this.container.addChild(chatBubble.container);
    }

    showEmoticon(emoticon: EmoticonInfo) {
        this.playEmoticonSound(emoticon);

        if (emoticon.type === 'text') {
            this.chatMessage(emoticon.text);
            return;
        }

        Assets.load<GifSource>(emoticon.imageUrl)
            .then((source) => {
                if (!this.container) {
                    return; // player destroyed while gif was loading
                }

                this.removeEmoticonSprite();

                const gifSprite: GifSprite = new GifSprite({
                    source,
                    loop: false,
                    autoPlay: true,
                    onComplete: () => setTimeout(() => {
                        if (this.emoticonSprite === gifSprite) {
                            this.removeEmoticonSprite();
                        }
                    })
                });

                const offsetTop = this.playerZoneHeight * 0.1;
                const size = this.playerAvatarCircleSize * 1.3;
                gifSprite.anchor.set(0.5);
                gifSprite.scale.set(size / Math.max(gifSprite.width, gifSprite.height));
                gifSprite.position.set(this.playerZoneWidth / 2, this.playerAvatarCircleSize / 2 + offsetTop - 20);

                this.emoticonSprite = gifSprite;
                this.container.addChild(gifSprite);
            })
            .catch((error) => console.error('Emoticon gif load failed', error));
    }

    private removeEmoticonSprite() {
        if (this.emoticonSprite) {
            if (this.emoticonSprite.parent) {
                this.emoticonSprite.parent.removeChild(this.emoticonSprite);
            }
            this.emoticonSprite.destroy();
            this.emoticonSprite = null;
        }
    }

    private playEmoticonSound(emoticon: EmoticonInfo) {
        if (!emoticon.soundUrl) {
            return;
        }

        try {
            const pathname = new URL(emoticon.soundUrl).pathname;
            if (!pathname.includes('.')) {
                return; // bare domain url = emoticon has no sound
            }
        } catch {
            return; // invalid url
        }

        const alias = `emoticon-${emoticon.id}`;
        if (sound.exists(alias)) {
            sound.play(alias);
        } else {
            sound.add(alias, { url: emoticon.soundUrl, preload: true, autoPlay: true });
        }
    }

    private _onOpenUserInfo() {
        this._store.dispatch(GamesActions.onOpenUserInfoDialog({
            tableId: this.tableId,
            playerId: this.playerId,
            playerName: this.name,
            noteColor: this.memberPreferences.playerColor[this.playerId],
            noteText: this.memberPreferences.playerNotes[this.playerId]
        }))
    }

    updateMemberPreferences(memberPreferences: MemberPreferences) {
        this.memberPreferences = memberPreferences;
        this._setPlayerNote(this.memberPreferences.playerColor[this.playerId])
    }

    private _setPlayerNote(noteColor?: keyof typeof NoteColors) {
        if (this.isMyPlayer) { return; }
        if (!noteColor) { // remove note if color is not provided
            if (this.playerNote) {
                this.container.removeChild(this.playerNote);
                this.playerNote.destroy();
                this.playerNote = undefined
            }
            return;
        }

        if (NoteColors[noteColor] === undefined) { // if color is not valid set default color
            noteColor = '#EBEBEB';
        }

        if (this.playerNote) { // if note already exists change texture
            this.playerNote.texture = this.textures.playerNote[noteColor];
            return;
        }

        // create new note
        this.playerNote = new Sprite(this.textures.playerNote[noteColor]);
        this.playerNote.anchor.set(0.5);
        this.playerNote.scale.set(0.6);
        this.playerNote.position.set(this.playerZoneWidth / 2 - this.infoBoxSprite.width / 2, this.playerZoneHeight - this.playerZoneHeight * 0.12);
        this.container.addChild(this.playerNote);
    }

    getPlayerNumbersOfCards(): number {
        return this.playerCardsController.cards.length;
    }

    revealCards(cards: CardData[]) {
        this.playerCardsController.removeAllCards();
        this.playerCardsController.addCards(cards, this.cardSorting);
    }

    revealR2TCards(cards: CardData[]) {
        // this.playerCardsController.removeAllCards();
        this.playerCardsController.addR2TCards(cards);
    }



    destroy() {
        this.destroy$.next();
        this.destroy$.complete();

        // Clean up chat bubbles
        for (const chatBubble of this.chatBubbles) {
            chatBubble.destroy();
        }
        this.chatBubbles = [];

        // Clean up emoticon gif
        this.removeEmoticonSprite();

        // Clean up call time
        if (this.callTime) {
            this.callTime.destroy?.();
        }

        // Clean up card replace info
        if (this.cardReplaceInfo) {
            this.cardReplaceInfo.destroy?.();
        }

        // Remove PIXI listeners
        this.infoBoxSprite?.removeAllListeners('pointerdown');

        // Clean up player cards controller
        this.playerCardsController?.destroy?.();

        // Clean up timebank
        if (this.timebank?.bar) {
            this.timebank.bar.destroy?.();
        }

        // 🔧 FIX: Explicitly destroy all sprites to prevent canvas memory leaks
        // Destroy avatar sprites
        if (this.avatarPictureSprite) {
            this.avatarPictureSprite.destroy({ children: true, texture: false });
            this.avatarPictureSprite = null as any;
        }
        if (this.avatarOverlaySprite) {
            this.avatarOverlaySprite.destroy({ children: true, texture: false });
            this.avatarOverlaySprite = null as any;
        }
        if (this.avatarOverlayBlinkSprite) {
            this.avatarOverlayBlinkSprite.destroy({ children: true, texture: false });
            this.avatarOverlayBlinkSprite = null as any;
        }

        // Destroy info box sprites
        if (this.infoBoxSprite) {
            this.infoBoxSprite.destroy({ children: true, texture: false });
            this.infoBoxSprite = null as any;
        }
        if (this.infoBoxBlinkSprite) {
            this.infoBoxBlinkSprite.destroy({ children: true, texture: false });
            this.infoBoxBlinkSprite = null as any;
        }

        // Destroy game indicator sprites
        if (this.dealerSprite) {
            this.dealerSprite.destroy({ children: true, texture: false });
            this.dealerSprite = null as any;
        }
        if (this.straddleSprite) {
            this.straddleSprite.destroy({ children: true, texture: false });
            this.straddleSprite = null as any;
        }
        if (this.proPlayerSprite) {
            this.proPlayerSprite.destroy({ children: true, texture: false });
            this.proPlayerSprite = null as any;
        }
        if (this.playerNote) {
            this.playerNote.destroy({ children: true, texture: false });
            this.playerNote = null as any;
        }

        // Destroy disconnection protection sprites
        if (this.disconnectionProtection) {
            if (this.disconnectionProtection.overlay) {
                this.disconnectionProtection.overlay.destroy({ children: true, texture: false });
            }
            if (this.disconnectionProtection.icon) {
                this.disconnectionProtection.icon.destroy({ children: true, texture: false });
            }
            if (this.disconnectionProtection.caption) {
                this.disconnectionProtection.caption.destroy({ children: true });
            }
            if (this.disconnectionProtection.timer) {
                this.disconnectionProtection.timer.destroy({ children: true });
            }
        }

        // Destroy text objects
        if (this.playerName) {
            this.playerName.destroy({ children: true });
            this.playerName = null as any;
        }
        if (this.playerAvatarText) {
            this.playerAvatarText.destroy({ children: true });
            this.playerAvatarText = null as any;
        }
        if (this.playerMoney) {
            if (this.playerMoney.amount) {
                this.playerMoney.amount.destroy({ children: true });
            }
            if (this.playerMoney.currency) {
                this.playerMoney.currency.destroy({ children: true });
            }
            if (this.playerMoney.decimal) {
                this.playerMoney.decimal.destroy({ children: true });
            }
            this.playerMoney = null as any;
        }
        if (this.timebank) {
            if (this.timebank.status) {
                this.timebank.status.destroy({ children: true });
            }
            if (this.timebank.timer) {
                this.timebank.timer.destroy({ children: true });
            }
            this.timebank = null as any;
        }

        // Destroy PIXI containers - destroy children recursively but not shared textures
        this.container?.removeChildren();
        this.container?.destroy({ children: true, texture: false });
        this.container = null as any;
    }
}
